import { formatCurrency } from "../../components/common/PriceDisplay";
import {
    DATE_RANGE_FIELD_TYPE,
    NUMERIC_RANGE_FIELD_TYPE,
    SELECT_FIELD_TYPE,
    TEXT_FIELD_TYPE,
     AUTOCOMPLETE_FIELD_TYPE
} from "../../helpers/constants";
import i18n from "../../i18n";
import { useBookingCancellationReasons, useBookingsStatuses } from "../../services/bookingsService";
import DateService from "../../services/dateService";
import { useInvestments, useProductTypes } from "../../services/productsService";
import { useGetSellers } from "../../services/usersApi";
import { getCalculatedPricingForProduct, useLocalizedProperty } from "../../utils";
import { BookingManageButton } from "./BookingManageButton";
import { BookingStatusText } from "./BookingStatusText";

export const BOOKINGS_TABLE_NAME = "bookings_table";

export const useBookingTableFieldsConfig = () => {
    const getLocalizedProperty = useLocalizedProperty();

    const { data: investments } = useInvestments({
        select: (data) =>
            data.results.map((investment) => ({
                value: investment.id,
                label: investment.investment_name_sales,
            })),
    });

    const { data: cancellationReasons, isLoading: isLoadingReasons } =
        useBookingCancellationReasons({
            select: (data) =>
                data.results.map((reason) => ({
                    value: reason.reason_name_eng,
                    label: getLocalizedProperty(reason, "reason_name"),
                })),
        });

    const { data: productTypesOptions } = useProductTypes(undefined, {
        select: (data) =>
            data.map((type) => ({
                id: type.id,
                label: getLocalizedProperty(type, "name"),
            })),
    });

    // Calculate duration in days between two dates
    const calculateDuration = (start, end) => {
        const startDate = new Date(start);
        const endDate = new Date(end);
        const differenceInTime = endDate.getTime() - startDate.getTime();
        const differenceInDays = Math.ceil(differenceInTime / (1000 * 3600 * 24)) + 1;
        return differenceInDays;
    };

    const { data: statuses } = useBookingsStatuses({
        select: (data) =>
            data.results.map((status) => ({
                value: status.status_name_eng,
                label: getLocalizedProperty(status, "status_name"),
            })),
    });

    const { data: sellers } = useGetSellers({
        select: (data) =>
            data.map((seller) => ({
                value: seller.user_id,
                label: `${seller.full_name}`,
            })),
    });

    return [
        {
            name: "name",
            getValueCallback: (rowData) =>
                rowData.customers.map((bc) => bc.customer.person?.name || "").join("\n"),
            label: i18n.t("table.bookings.name"),
            filterType: TEXT_FIELD_TYPE,
            getFilterFieldConfigCallback: (filteringData) => undefined,
            sortable: true,
        },
        {
            name: "surname",
            getValueCallback: (rowData) =>
                rowData.customers.map((bc) => bc.customer.person?.surname || "").join("\n"),
            label: i18n.t("table.bookings.surname"),
            filterType: TEXT_FIELD_TYPE,
            getFilterFieldConfigCallback: (filteringData) => undefined,
            sortable: true,
        },
        {
            name: "companyName",
            getValueCallback: (rowData) =>
                rowData.customers.map((bc) => bc.customer.company?.name || "").join("\n"),
            label: i18n.t("table.bookings.companyName"),
            filterType: TEXT_FIELD_TYPE,
            getFilterFieldConfigCallback: (filteringData) => undefined,
            sortable: true,
        },
        {
            name: "investment",
            getValueCallback: (rowData) =>
                rowData.products.length > 0
                    ? rowData.products
                          .map((product) => product.investment.investment_name_sales)
                          .join("\n")
                    : "",
            label: i18n.t("table.bookings.investment"),
            filterType: SELECT_FIELD_TYPE,
            getFilterFieldConfigCallback: (filteringData) => ({
                optionKey: "value",
                optionLabel: "label",
                options: investments,
                multiple: false,
            }),
            fieldConfig: {
                optionKey: "value",
                optionLabel: "label",
                options: investments,
            },
            sortable: false,
            minWidth: 120,
            width: 120,
        },
        {
            name: "status",
            getValueCallback: (rowData) => <BookingStatusText status={rowData.status} />,
            label: i18n.t("table.bookings.status"),
            filterType: SELECT_FIELD_TYPE,
            sortable: true,
            getFilterFieldConfigCallback: (filteringData) => ({
                options: statuses,
                optionKey: "value",
                optionLabel: "label",
            }),
            fieldConfig: {
                optionKey: "value",
                optionLabel: "label",
                options: statuses,
            },
        },
        {
            name: "dateFrom",
            getValueCallback: (rowData) =>
                DateService.convertDatetimeFromBackendToFormatYYYYMMDD(rowData.start),
            label: i18n.t("table.bookings.dateFrom"),
            filterType: DATE_RANGE_FIELD_TYPE,
            getFilterFieldConfigCallback: (filteringData) => ({
                style: { minWidth: "100px" },
            }),
            sortable: true,
        },
        {
            name: "dateTo",
            getValueCallback: (rowData) =>
                DateService.convertDatetimeFromBackendToFormatYYYYMMDD(rowData.end),
            label: i18n.t("table.bookings.dateTo"),
            filterType: DATE_RANGE_FIELD_TYPE,
            getFilterFieldConfigCallback: (filteringData) => ({
                style: { minWidth: "100px" },
            }),
            sortable: true,
        },
        {
            name: "duration",
            getValueCallback: (rowData) => calculateDuration(rowData.start, rowData.end),
            label: i18n.t("table.bookings.duration"),
            filterType: NUMERIC_RANGE_FIELD_TYPE,
            getFilterFieldConfigCallback: (filteringData) => undefined,
            sortable: true,
        },
        {
            name: "creator",
            getValueCallback: (rowData) => rowData.creator,
            label: i18n.t("table.bookings.creator"),
            filterType: SELECT_FIELD_TYPE,
            getFilterFieldConfigCallback: (filteringData) => ({
                options: sellers,
                optionKey: "value",
                optionLabel: "label",
            }),
            fieldConfig: {
                optionKey: "value",
                optionLabel: "label",
                options: sellers,
            },
            sortable: true,
        },
        {
            name: "note",
            getValueCallback: (rowData) => rowData.note || "",
            label: i18n.t("table.bookings.note"),
            filterType: TEXT_FIELD_TYPE,
            getFilterFieldConfigCallback: (filteringData) => undefined,
            sortable: true,
        },
        {
            name: "cancellationReason",
            getValueCallback: (rowData) => getLocalizedProperty(rowData.reason, "reason_name"),
            label: i18n.t("table.bookings.cancellationReason"),
            sortable: true,
            filterType: SELECT_FIELD_TYPE,
            getFilterFieldConfigCallback: (filteringData) =>
                isLoadingReasons
                    ? undefined
                    : {
                          optionKey: "value",
                          optionLabel: "label",
                          options: cancellationReasons,
                          multiple: false,
                      },
            fieldConfig: isLoadingReasons
                ? undefined
                : {
                      optionKey: "value",
                      optionLabel: "label",
                      options: cancellationReasons,
                  },
        },
        {
            name: "status_note",
            getValueCallback: (rowData) => rowData.status_note,
            label: i18n.t("table.bookings.statusNote"),
            filterType: TEXT_FIELD_TYPE,
            getFilterFieldConfigCallback: (_) => undefined,
        },
        {
            name: "productTypeId",
            other_export_field_name: "productType",
            getValueCallback: (rowData) =>
                rowData.products.length > 0
                    ? rowData.products
                          .map((product) =>
                              product.type ? getLocalizedProperty(product.type, "name") : "–",
                          )
                          .join("\n")
                    : "",
            label: i18n.t("table.bookings.productType"),
            filterType: AUTOCOMPLETE_FIELD_TYPE,
            minWidth: 120,
            width: 120,
            fieldConfig: {
                optionLabelKey: "label",
                options: productTypesOptions || [],
                multiple: true,
                isObjectOption: true,
                disableClearable: false
            },
            sortable: false,
        },
        {
            name: "productNumber",
            getValueCallback: (rowData) =>
                rowData.products.length > 0
                    ? rowData.products.map((product) => product.product_number).join("\n")
                    : "",
            label: i18n.t("table.bookings.productNumber"),
            filterType: TEXT_FIELD_TYPE,
            getFilterFieldConfigCallback: (filteringData) => undefined,
            sortable: true,
        },
        {
            name: "pricingListedGross",
            getValueCallback: (rowData) =>
                rowData.products.length > 0
                    ? rowData.products
                          .map((product) => {
                              const pricing = getCalculatedPricingForProduct(product);
                              return formatCurrency(pricing.listed.gross);
                          })
                          .join("\n")
                    : "",
            label: i18n.t("table.bookings.pricingListedGross"),
            sortable: false,
        },
        {
            name: "pricingListedNet",
            getValueCallback: (rowData) =>
                rowData.products.length > 0
                    ? rowData.products
                          .map((product) => {
                              const pricing = getCalculatedPricingForProduct(product);
                              return formatCurrency(pricing.listed.net);
                          })
                          .join("\n")
                    : "",
            label: i18n.t("table.bookings.pricingListedNet"),
            sortable: false,
        },
        {
            name: "pricingListedGrossPerSqm",
            getValueCallback: (rowData) =>
                rowData.products.length > 0
                    ? rowData.products
                          .map((product) => {
                              const pricing = getCalculatedPricingForProduct(product);
                              return formatCurrency(pricing.listed.gross_per_sqm);
                          })
                          .join("\n")
                    : "",
            label: i18n.t("table.bookings.pricingListedGrossPerSqm"),
            sortable: false,
        },
        {
            name: "pricingListedNetPerSqm",
            getValueCallback: (rowData) =>
                rowData.products.length > 0
                    ? rowData.products
                          .map((product) => {
                              const pricing = getCalculatedPricingForProduct(product);
                              return formatCurrency(pricing.listed.net_per_sqm);
                          })
                          .join("\n")
                    : "",
            label: i18n.t("table.bookings.pricingListedNetPerSqm"),
            sortable: false,
        },
        {
            name: "manageBooking",
            getValueCallback: (rowData) => <BookingManageButton booking={rowData} />,
            label: i18n.t("table.bookings.manage"),
            getFilterFieldConfigCallback: (filteringData) => undefined,
            sortable: false,
            excludedFromExport: true,
            allowChangeVisibility: false,
        },
    ];
};
